/* WORDCHARTS — the app's own small SVG chart kit (docs/project/word-history.md).
 *
 * Hand-rolled on purpose: the app vendors no chart library and the four shapes the Word history
 * screen needs (a line over dots, bars, a step line, edge marks) are a few hundred lines against
 * a dependency that would outweigh the rest of static/js. Precedents for drawing by hand: the
 * sparkline (main.js), the garden sprite (garden.js), landing_garden's hillSvg().
 *
 * ── The two rules everything here follows ───────────────────────────────────────────────────
 * 1. SVG is built with createElementNS, NEVER through jQuery — jQuery 1.9's $("<svg>…") creates
 *    HTML-namespace elements that parse fine and render as nothing (the garden.js:15 trap).
 * 2. NO viewBox scaling: the SVG is drawn at the container's measured width, 1 SVG px = 1 CSS px,
 *    so the axis text is exactly the size the CSS says (.85rem) and can never be shrunk by a
 *    transform. The owner's standing rule is "don't make the font too small", and a viewBox would
 *    make the font whatever the division happens to produce. The history card is fixed-size, so
 *    a chart drawn at open time needs no resize handling.
 *
 * Colors come from the design tokens via CSS classes (css/components/chart.css) — nothing here
 * names a color. Tooltips ride as data-tip attributes read by ONE PL.tip delegate for all charts,
 * the sparkline's own pattern; every dot/bar gets an invisible fat hit target because a 3px dot
 * is not a hover surface.
 */
window.WORDCHARTS = (function () {
	"use strict";

	var NS = "http://www.w3.org/2000/svg";

	// Frame paddings, px. LEFT is for the y labels' text and set generously — a clipped "12.5s"
	// is worse than a narrower plot; BOTTOM is one line of x labels.
	var PAD_L = 46, PAD_R = 12, PAD_T = 10, PAD_B = 24;

	var tipDelegateArmed = false;

	// Own escaper — main.js's escHtml is private to its closure, and a tip readout composed from
	// data attributes must never reach the tip engine as markup.
	function esc(s) {
		return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	function svgEl(name, attrs) {
		var el = document.createElementNS(NS, name);
		for (var k in attrs) {
			if (attrs.hasOwnProperty(k)) { el.setAttribute(k, attrs[k]); }
		}
		return el;
	}

	/** ~n "nice" tick values over [0, max] (all charts here grow up from a zero baseline —
	 *  a response-time or accuracy axis that starts mid-air flatters every trend it shows). */
	function niceTicks(max, n) {
		if (max <= 0) { return [0, 1]; }
		var rawStep = max / n;
		var mag = Math.pow(10, Math.floor(Math.log(rawStep) / Math.LN10));
		var step = mag;
		var mults = [1, 2, 2.5, 5, 10];
		for (var i = 0; i < mults.length; i++) {
			if (mag * mults[i] >= rawStep) { step = mag * mults[i]; break; }
		}
		var ticks = [];
		for (var v = 0; v <= max + step * 0.001; v += step) { ticks.push(v); }
		return ticks;
	}

	function fmtShortDate(ms) {
		return new Date(ms).toLocaleDateString(localeTag(), { month: "short", day: "numeric" });
	}

	/**
	 * Draw one chart into `container` (emptied first). Options:
	 *   height   px number (the plot's total height)
	 *   series   [{kind, pts, cls, r?}] — kinds:
	 *              line   pts [{x,y,tip?}] joined by a polyline, a dot per point
	 *              dots   pts [{x,y,tip?}] scatter only
	 *              bars   pts [{x,y,tip?,cls?}] vertical bars up from 0
	 *              step   pts [{x,y,tip?}] step-after line (holds each y until the next x)
	 *              marks  pts [{x,tip?}] ✗ marks along the TOP edge — events with no y of
	 *                     their own (a revealed showing has no answer time to place)
	 *   yMax     optional y ceiling (else the data's nice maximum)
	 *   yTicks   optional explicit tick values — for axes where fractions would be nonsense
	 *            (a plant stage of 2.5, an accuracy gridline at 37.5%)
	 *   yFmt     value → label ("2.4s", "12d", "80%"); also used by default tips
	 *   xFmt     x → label under the axis (defaults to a short date — x is a timestamp unless
	 *            the caller says otherwise)
	 *   xTicks   optional [{x, label}] — for index-scaled charts where only the caller knows
	 *            which indices deserve a date under them
	 * Returns the svg element (appended), or null when there was nothing to draw.
	 */
	function render(container, opts) {
		armTipDelegate();
		container.innerHTML = "";

		var width = Math.max(200, Math.round(container.getBoundingClientRect().width) || 480);
		var height = opts.height || 180;
		var plotW = width - PAD_L - PAD_R;
		var plotH = height - PAD_T - PAD_B;

		// Domains from the data; a single point still deserves a real frame, so a degenerate x
		// span is padded half a day each side rather than divided by zero.
		var xMin = Infinity, xMax = -Infinity, yTop = 0, hasPts = false;
		var barCount = 0;
		for (var s = 0; s < opts.series.length; s++) {
			var pts = opts.series[s].pts;
			if (opts.series[s].kind === "bars") { barCount = Math.max(barCount, pts.length); }
			for (var i = 0; i < pts.length; i++) {
				hasPts = true;
				xMin = Math.min(xMin, pts[i].x);
				xMax = Math.max(xMax, pts[i].x);
				if (pts[i].y != null) { yTop = Math.max(yTop, pts[i].y); }
			}
		}
		if (!hasPts) { return null; }
		if (xMax - xMin < 1e-9) { xMin -= 43200000; xMax += 43200000; }
		if (barCount > 1) {
			// Half a slot of air each side, so the first and last bar stand inside the frame
			// rather than straddling its edges.
			var slot = (xMax - xMin) / (barCount - 1) / 2;
			xMin -= slot;
			xMax += slot;
		}

		var ticks = opts.yTicks || niceTicks(opts.yMax != null ? opts.yMax : yTop, 3);
		var yMax = opts.yMax != null ? opts.yMax : ticks[ticks.length - 1];
		if (yMax <= 0) { yMax = 1; }

		function X(x) { return PAD_L + (x - xMin) / (xMax - xMin) * plotW; }
		function Y(y) { return PAD_T + plotH - Math.min(y, yMax) / yMax * plotH; }

		var svg = svgEl("svg", { "class": "chartSvgClass", width: width, height: height });

		// y gridlines + labels. The zero line doubles as the x axis.
		for (var t = 0; t < ticks.length; t++) {
			if (ticks[t] > yMax + 1e-9) { continue; }
			var gy = Y(ticks[t]);
			svg.appendChild(svgEl("line", {
				"class": ticks[t] === 0 ? "chAxisLineClass" : "chGridClass",
				x1: PAD_L, y1: gy, x2: width - PAD_R, y2: gy
			}));
			var lbl = svgEl("text", {
				"class": "chAxisTextClass", x: PAD_L - 6, y: gy + 4, "text-anchor": "end"
			});
			lbl.textContent = opts.yFmt ? opts.yFmt(ticks[t]) : String(ticks[t]);
			svg.appendChild(lbl);
		}

		// x labels: what the caller pinned, or 3 evenly spaced values formatted as dates.
		var xFmt = opts.xFmt || fmtShortDate;
		var xTicks = opts.xTicks;
		if (!xTicks) {
			xTicks = [];
			var n = plotW > 420 ? 3 : 2;
			for (var xt = 0; xt <= n; xt++) {
				var xv = xMin + (xMax - xMin) * xt / n;
				xTicks.push({ x: xv, label: xFmt(xv) });
			}
		}
		for (var x2 = 0; x2 < xTicks.length; x2++) {
			var tx = X(xTicks[x2].x);
			// First label left-anchored, last right-anchored, so neither leaves the frame.
			var anchor = x2 === 0 ? "start" : (x2 === xTicks.length - 1 ? "end" : "middle");
			var xl = svgEl("text", {
				"class": "chAxisTextClass", x: tx, y: height - 6, "text-anchor": anchor
			});
			xl.textContent = xTicks[x2].label;
			svg.appendChild(xl);
		}

		for (var s2 = 0; s2 < opts.series.length; s2++) {
			drawSeries(svg, opts.series[s2], X, Y, plotW, opts);
		}

		container.appendChild(svg);
		return svg;
	}

	function drawSeries(svg, series, X, Y, plotW, opts) {
		var pts = series.pts;
		if (!pts.length) { return; }
		var cls = series.cls || "";
		var i, p;

		if (series.kind === "line" || series.kind === "step") {
			var d = "";
			for (i = 0; i < pts.length; i++) {
				var px = X(pts[i].x), py = Y(pts[i].y);
				if (!i) {
					d = "M" + px + " " + py;
				} else if (series.kind === "step") {
					d += " H" + px + " V" + py;   // hold the old value until the new x
				} else {
					d += " L" + px + " " + py;
				}
			}
			svg.appendChild(svgEl("path", { "class": "chLineClass " + cls, d: d }));
		}

		if (series.kind === "bars") {
			// Bars shrink to keep a gap when points crowd; 3px is the readability floor. The
			// width also respects the SMALLEST gap between neighbouring bars, not just their
			// count: on a real calendar axis (practice time by day) two adjacent days sit one
			// day apart however long the whole span is, and a count-based width overlapped them.
			var minGap = plotW;
			var xs = [];
			for (i = 0; i < pts.length; i++) { xs.push(X(pts[i].x)); }
			xs.sort(function (a, b) { return a - b; });
			for (i = 1; i < xs.length; i++) { minGap = Math.min(minGap, xs[i] - xs[i - 1]); }
			var bw = Math.max(3, Math.min(18, Math.floor(plotW / pts.length * 0.6),
				Math.floor(minGap * 0.7)));
			for (i = 0; i < pts.length; i++) {
				p = pts[i];
				var by = Y(p.y);
				var rect = svgEl("rect", {
					"class": "chBarClass " + cls + " " + (p.cls || ""),
					x: X(p.x) - bw / 2, y: by,
					width: bw, height: Math.max(1, Y(0) - by)
				});
				svg.appendChild(rect);
				hit(svg, X(p.x), (by + Y(0)) / 2, Math.max(bw, 14), p, opts);
			}
			return;
		}

		if (series.kind === "marks") {
			// ✗ along the top edge: the event happened at x and has no y (a revealed showing
			// has no answer time). Drawn as a small cross, not a dot, so it cannot be read as
			// a data point that happens to be maximal.
			for (i = 0; i < pts.length; i++) {
				p = pts[i];
				var mx = X(p.x), my = PAD_T + 5, r = 3.2;
				svg.appendChild(svgEl("path", {
					"class": "chMarkClass " + cls,
					d: "M" + (mx - r) + " " + (my - r) + " L" + (mx + r) + " " + (my + r)
						+ " M" + (mx - r) + " " + (my + r) + " L" + (mx + r) + " " + (my - r)
				}));
				hit(svg, mx, my, 14, p, opts);
			}
			return;
		}

		// line's own dots, or a plain scatter
		if (series.kind === "line" || series.kind === "dots" || series.kind === "step") {
			for (i = 0; i < pts.length; i++) {
				p = pts[i];
				if (p.y == null) { continue; }
				svg.appendChild(svgEl("circle", {
					"class": "chDotClass " + cls + " " + (p.cls || ""),
					cx: X(p.x), cy: Y(p.y), r: series.r || 3
				}));
				hit(svg, X(p.x), Y(p.y), 12, p, opts);
			}
		}
	}

	/** The invisible fat hover target a 3px dot cannot be — carries the tip text. */
	function hit(svg, cx, cy, r, p, opts) {
		var tip = p.tip != null ? p.tip
			: (opts.yFmt && p.y != null ? opts.yFmt(p.y) : null);
		if (tip == null) { return; }
		svg.appendChild(svgEl("circle", {
			"class": "chHitClass", cx: cx, cy: cy, r: Math.max(9, r), "data-tip": tip
		}));
	}

	// ONE readout delegate for every chart ever drawn — the sparkline's own pattern. Armed on
	// first render, not at parse time: PL is an ES module and this file is a classic script, so
	// PL does not exist yet when this line is first read.
	function armTipDelegate() {
		if (tipDelegateArmed || !window.PL || !PL.tip) { return; }
		tipDelegateArmed = true;
		PL.tip.delegate(document, ".chartSvgClass .chHitClass[data-tip]", {
			guided: false,
			content: function (el) { return esc(el.getAttribute("data-tip")); }
		});
	}

	return {
		render: render,
		fmtShortDate: fmtShortDate
	};
})();
